import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import CustomNavbar from "components/users/CustomNavbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faQuestionCircle,
  faInfoCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  customYellowColor,
  primaryColor,
  primaryColorOpacity,
  secondaryColor,
} from "utils/colors";
import logo from "assets/logo.png";
import {
  activatePromotion,
  createAuthenticatedAxiosInstance,
  deactivatePromotion,
  fetchPromotionById,
} from "api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import CustomMultiSelect, {
  OptionType,
} from "components/general/CustomMultiSelect";
import LoadingScreen from "components/general/LoadingScreen";
import CustomBackButton from "components/general/CustomBackButton";
import "./PromotionDetails.scss";
import ImageModal from "components/general/ImageModal";
import CancelContinue from "components/general/CancelContinueModal";
import { es } from 'date-fns/locale';

const PromotionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [promotionName, setPromotionName] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [discountValue, setDiscountValue] = useState("");
  const [discountType, setDiscountType] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<OptionType[]>(
    []
  );
  const [selectedProducts, setSelectedProducts] = useState<OptionType[]>([]);
  const [image, setImage] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);

  /**
   * Effect hook to fetch and load promotion data
   * @effect
   * @dependencies []
   *
   * The effect:
   * 1. Makes API call to fetch promotion by ID
   * 2. Updates all form states with promotion data:
   *    - Basic info (name, description, discount)
   *    - Parses and sets dates and times
   *    - Maps categories and products to select options
   * 3. Manages loading state
   * 4. Handles error cases
   *
   * @async
   * @function fetchPromotionData
   * @throws {Error} When API call fails
   */
  useEffect(() => {
    const fetchPromotionData = async () => {
      try {
        const promotion = await fetchPromotionById(axiosInstance, Number(id));

        setPromotionName(promotion.name);
        setDescription(promotion.description);
        setDiscountValue(promotion.discount.toString());
        setDiscountType(promotion.type);
        setStatus(promotion.status);
        setImage(promotion.images[0] || "");

        // Parse dates
        const startDateTime = new Date(promotion.start_date);
        const endDateTime = new Date(promotion.end_date);

        setStartDate(startDateTime);
        setStartTime(startDateTime);
        setEndDate(endDateTime);
        setEndTime(endDateTime);

        // Set categories and products as disabled options
        setSelectedCategories(
          promotion.categories.map((category) => ({
            value: category.id.toString(),
            label: category.name,
          }))
        );

        setSelectedProducts(
          promotion.products.map((product) => ({
            value: product.id.toString(),
            label: product.name,
          }))
        );

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching promotion:", error);
        setIsLoading(false);
      }
    };

    fetchPromotionData();
  }, []);

  /**
   * Handles the confirmation of promotion status toggle
   * @function handleConfirmToggle
   * @async
   *
   * The function:
   * 1. Deactivates or activates promotion based on current status
   * 2. Fetches updated promotion data
   * 3. Updates UI state with new status
   * 4. Closes confirmation modal
   * 5. Handles any errors during the process
   */
  const handleConfirmToggle = async () => {
    try {
      if (status) {
        await deactivatePromotion(axiosInstance, Number(id));
      } else {
        await activatePromotion(axiosInstance, Number(id));
      }
      const updatedPromotion = await fetchPromotionById(
        axiosInstance,
        Number(id)
      );
      setStatus(updatedPromotion.status);
      setShowToggleModal(false);
    } catch (error) {
      console.error("Error toggling promotion status:", error);
    }
  };

  /**
   * Navigates to promotion edit page
   * @function handleEdit
   *
   * Redirects user to the edit form for current promotion
   */
  const handleEdit = () => {
    navigate(`/promotions/${id}/edit`);
  };

  /**
   * Shows confirmation modal for status toggle
   * @function handleToggleStatus
   *
   * Opens modal to confirm activation/deactivation of promotion
   */
  const handleToggleStatus = () => {
    setShowToggleModal(true);
  };

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={() => navigate("/dashboard#promociones")} />

      <Container className="px-4 px-md-5 promotion-details">
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" style={{ width: "175px" }} />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col className="text-center">
            <h1>Detalles de la Promoción</h1>
          </Col>
        </Row>

        {isLoading ? (
          <LoadingScreen loadingText="Cargando información de la promoción..." />
        ) : (
          <Form>
            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <Row className="mb-3">
                  <Col xs={6}>
                    <Form.Label className="fw-bold">No.</Form.Label>
                    <div className="d-flex align-items-center">
                      <span style={{ fontSize: "24px", color: "#747775" }}>
                        {id}
                      </span>
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="right"
                        overlay={
                          <Tooltip className="custom-tooltip">
                            ID único de la promoción
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                      </OverlayTrigger>
                    </div>
                  </Col>
                  <Col xs={6} className="text-end">
                    <OverlayTrigger
                      placement="left"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          Estado actual de la promoción
                        </Tooltip>
                      }
                    >
                      <span
                        className={`status-badge ${
                          status ? "active" : "inactive"
                        }`}
                      >
                        {status ? "Activo" : "Inactivo"}
                      </span>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Nombre de la promoción
                  </Form.Label>
                  <Form.Control type="text" value={promotionName} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8}>
                <Card
                  style={{
                    backgroundColor: primaryColorOpacity,
                    border: `2px solid ${primaryColor}`,
                    boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.4)",
                    margin: "1rem 0rem",
                  }}
                >
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            Fecha inicio
                          </Form.Label>
                          <div className="d-flex align-items-center">
                            <DatePicker
                              locale={es}
                              selected={startDate}
                              dateFormat="dd/MM/yyyy"
                            className="form-control date-picker-position"
                              readOnly
                            />
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="ms-2"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            Hora inicio
                          </Form.Label>
                          <div className="d-flex align-items-center">
                            <DatePicker
                              locale={es}
                              selected={startTime}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              dateFormat="h:mm aa"
                            className="form-control date-picker-position"
                              readOnly
                            />
                            <FontAwesomeIcon icon={faClock} className="ms-2" />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Fecha fin</Form.Label>
                          <div className="d-flex align-items-center">
                            <DatePicker
                              locale={es}
                              selected={endDate}
                              dateFormat="dd/MM/yyyy"
                            className="form-control date-picker-position"
                              readOnly
                            />
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="ms-2"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Hora fin</Form.Label>
                          <div className="d-flex align-items-center">
                            <DatePicker
                              locale={es}
                              selected={endTime}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              dateFormat="h:mm aa"
                              className="form-control date-picker-position"
                              readOnly
                            />
                            <FontAwesomeIcon icon={faClock} className="ms-2" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-center w-100">
                    Descuento
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          Descuento aplicado en la promoción
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        className="ms-2"
                      />
                    </OverlayTrigger>
                  </Form.Label>
                  <div className="d-flex justify-content-center">
                    <div className="d-flex rounded overflow-hidden">
                      {discountType === "$" && (
                        <div
                          className="px-2 py-1 text-white"
                          style={{
                            backgroundColor: secondaryColor,
                            borderTopLeftRadius: "1rem",
                            borderBottomLeftRadius: "1rem",
                          }}
                        >
                          <span className="fw-bold">{discountType}</span>
                        </div>
                      )}
                      <Form.Control
                        value={discountValue}
                        readOnly
                        style={{
                          width: "4.5rem",
                          borderRadius: 0,
                          backgroundColor: customYellowColor,
                          textAlign: "center",
                          height: "2rem",
                          padding: "0.25rem",
                        }}
                      />
                      {discountType === "%" && (
                        <div
                          className="px-2 py-1 text-white"
                          style={{
                            backgroundColor: secondaryColor,
                            borderTopRightRadius: "1rem",
                            borderBottomRightRadius: "1rem",
                          }}
                        >
                          <span className="fw-bold">{discountType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={description}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Categorías aplicables
                  </Form.Label>
                  <CustomMultiSelect
                    options={selectedCategories}
                    value={selectedCategories}
                    onChange={() => {}}
                    isDisabled={true}
                    placeholder="Categorías seleccionadas"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Productos aplicables
                  </Form.Label>
                  <CustomMultiSelect
                    options={selectedProducts}
                    value={selectedProducts}
                    onChange={() => {}}
                    isDisabled={true}
                    placeholder="Productos seleccionados"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Imagen representativa
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          Imagen que identifica la promoción
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        className="ms-2"
                      />
                    </OverlayTrigger>
                  </Form.Label>

                  <div className="d-flex justify-content-center">
                    {image && (
                      <div
                        style={{
                          width: "9.5rem",
                          height: "9.5rem",
                          borderRadius: "9px",
                          overflow: "hidden",
                          position: "relative",
                          cursor: "pointer",
                        }}
                        onClick={() => setModalOpen(true)}
                      >
                        <img
                          src={
                            image.startsWith("data:image")
                              ? image
                              : image.startsWith("http")
                              ? image
                              : `data:image/jpeg;base64,${image}`
                          }
                          alt="Promotion"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
        <Row className="justify-content-center mb-4">
          <Col md={8}>
            <Row className="mt-4 mb-5">
              <Col>
                <Button
                  className="bg-secundary"
                  variant="primary"
                  onClick={handleToggleStatus}
                >
                  {status ? "Desactivar promoción" : "Activar promoción"}
                </Button>
              </Col>
              <Col className="text-end">
                <Button variant="primary" onClick={handleEdit}>
                  Editar promoción
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        images={image ? [image] : []}
        selectedIndex={0}
        onSelectImage={() => {}}
        isReadOnly={true}
        isSingleImage={true}
      />
      <CancelContinue
        show={showToggleModal}
        onHide={() => setShowToggleModal(false)}
        onConfirm={handleConfirmToggle}
        title={`${status ? "Desactivar" : "Activar"} promoción`}
        message={`¿Estás seguro que deseas ${
          status ? "desactivar" : "activar"
        } esta promoción?`}
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />
    </>
  );
};

export default PromotionDetails;
